/**
 * Flow → Orders (PLAN_FLOW_PRODUK.md Fasa 2, 30 Sept 2026) — setiap resit, BACA SAHAJA.
 *
 * Dua sumber digabung semasa memapar (simpanan kekal asing — lihat firestore.rules):
 *   • orders (type flowultra)  — bayaran Billplz
 *   • flow_tenants dengan paidAt — customer yang admin masuk manual
 * Resit manual TIDAK dicipta sebagai rekod baharu — Dashboard (jualanManualFlow)
 * sudah mengira flow_tenants terus; rekod kedua = jualan dikira dua kali.
 */
import { useMemo, useState } from 'react';
import { Badge } from '../design/ui';
import { produkUntuk, rmDariSen } from '../services/flowProdukService';

const tarikh = (d) => (d ? new Date(d).toLocaleDateString('en-MY', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kuala_Lumpur',
}) : '—');

export default function FlowOrdersScreen({ orders, customers, produk, onOpenCustomer }) {
    const [tapis, setTapis] = useState('paid');
    const [sumber, setSumber] = useState('all');

    const baris = useMemo(() => {
        const subIkutId = new Map(customers.filter((c) => c.kind !== 'tenant').map((c) => [c.id, c]));
        const billplz = orders.map((o) => {
            const c = (o.flowSubId && subIkutId.get(o.flowSubId)) || null;
            const p = o.productId ? produk.find((x) => x.id === o.productId) : null;
            return {
                key: `o:${o.id}`, at: o.paidAt || o.createdAt, c,
                who: c?.name || o.name || c?.phone || o.phone || o.email,
                email: o.email,
                product: p?.name || o.productName || (c ? produkUntuk(c, produk)?.name : '') || 'Flow Ultra',
                /* Resit manual (Extend days → Paid renewal, 2026-10-14) duduk dalam `orders` juga — provider 'manual'. */
                sen: o.amountSen, renew: o.isRenewal, source: o.provider === 'manual' ? 'Manual' : 'Billplz',
                channel: o.provider === 'manual' ? 'Transfer' : o.channel, status: o.status, receipt: o.receipt, days: o.days,
            };
        });
        // Bayaran MANUAL asal (manualPaidAt) — kekal walaupun customer itu kemudian renew melalui Billplz.
        const manual = customers.filter((c) => c.manual && c.manualPaidAt).map((c) => ({
            key: `t:${c.id}`, at: c.manualPaidAt, c,
            who: c.name || c.phone || c.email, email: c.email,
            product: produkUntuk(c, produk)?.name || `Type ${c.jenis}`,
            sen: c.manualSen || 0, renew: false, source: 'Manual',
            channel: 'Transfer', status: 'paid', receipt: '', days: null,
        }));
        return [...billplz, ...manual]
            .filter((r) => (tapis === 'all' || r.status === 'paid'))
            .filter((r) => (sumber === 'all' || r.source === sumber))
            .sort((a, b) => (b.at?.getTime?.() || 0) - (a.at?.getTime?.() || 0));
    }, [orders, customers, produk, tapis, sumber]);

    const jumlah = baris.filter((r) => r.status === 'paid').reduce((s, r) => s + (r.sen || 0), 0);

    return (
        <>
            <div className="s-sec">
                <div>
                    <h2>Orders</h2>
                    <p>Every payment — one row per receipt. A renewal is a new receipt on the same subscription.</p>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', margin: '0 0 10px' }}>
                <select className="s-inp" style={{ width: 'auto' }} value={tapis} onChange={(e) => setTapis(e.target.value)}>
                    <option value="paid">Paid only</option>
                    <option value="all">All, including unpaid</option>
                </select>
                <select className="s-inp" style={{ width: 'auto' }} value={sumber} onChange={(e) => setSumber(e.target.value)}>
                    <option value="all">Billplz and manual</option>
                    <option value="Billplz">Billplz only</option>
                    <option value="Manual">Manual only</option>
                </select>
                <span className="s-dim" style={{ marginLeft: 'auto' }}>
                    {baris.length} receipt{baris.length === 1 ? '' : 's'} · <b>{rmDariSen(jumlah)}</b> paid
                </span>
            </div>

            <div className="fu-tblwrap">
                <table className="fu-tbl">
                    <thead>
                        <tr><th>Date</th><th>Customer</th><th>Product</th><th>Amount</th><th>Kind</th><th>Source</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                        {baris.length === 0
                            ? <tr><td className="fu-tbl__none" colSpan={7}>No receipt matches this filter.</td></tr>
                            : baris.map((r) => (
                                <tr key={r.key} onClick={() => r.c && onOpenCustomer(r.c)} style={r.c ? undefined : { cursor: 'default' }}>
                                    <td className="s-num">{tarikh(r.at)}</td>
                                    <td>
                                        <span className="fu-tbl__who">{r.who}</span>
                                        {r.email && r.email !== r.who && <span className="s-dim" style={{ display: 'block', fontSize: 12 }}>{r.email}</span>}
                                    </td>
                                    <td>{r.product}{r.days ? <span className="s-dim"> · {r.days} days</span> : null}</td>
                                    <td className="s-num">{r.sen ? rmDariSen(r.sen) : '—'}</td>
                                    <td>{r.renew ? 'Renewal' : 'New'}</td>
                                    <td>
                                        {r.source}
                                        {r.receipt && (
                                            <a href={r.receipt} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                                                style={{ display: 'block', fontSize: 12 }}>Receipt</a>
                                        )}
                                    </td>
                                    <td>
                                        <Badge tone={r.status === 'paid' ? 'ok' : r.status === 'failed' ? 'stop' : 'neutral'}>
                                            {r.status === 'paid' ? 'Paid' : r.status === 'failed' ? 'Failed' : 'Unpaid'}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
            <p className="s-kv__hint" style={{ marginTop: 10 }}>
                Manual receipts come from customers you added by hand (their first payment) and from renewals you
                recorded with Extend days → Paid renewal, using the payment date you entered.
                Receipts without a subscription (unpaid bills) cannot be opened.
            </p>
        </>
    );
}
